import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Cards from 'react-credit-cards-2';
import 'react-credit-cards-2/dist/es/styles-compiled.css';
import RightSideSummary from "./RightSideSummary";
import {
  CreditCardIcon,
  UserIcon,

  ExclamationCircleIcon,
  BuildingOffice2Icon,
} from "@heroicons/react/24/outline";

// Validation utility functions
const validationRules = {
  cardNumber: {
    required: true,
    pattern: /^\d{13,16}$/, // Updated: 13-16 digits (not 19)
    message: "Please enter a valid card number (13-16 digits)",
    luhn: true
  },
  cardName: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z\s.'-]+$/,
    message: "Please enter a valid name"
  },
  cardExpiry: {
    required: true,
    pattern: /^(0[1-9]|1[0-2])\/\d{2}$/,
    message: "Please enter a valid expiry date (MM/YY)",
    futureDate: true
  },
  cardCvc: {
    required: true,
    pattern: /^\d{3,4}$/,
    message: "Please enter a valid CVV (3-4 digits)"
  },
  street: {
    required: true,
    minLength: 5,
    maxLength: 200,
    message: "Please enter a valid street address"
  },
  city: {
    required: true,
    minLength: 2,
    maxLength: 100,
    message: "Please enter a valid city"
  },
  state: {
    required: true,
    minLength: 2,
    maxLength: 50,
    message: "Please enter a valid state"
  },
  zipCode: {
    required: true,
    pattern: /^\d{5}(-\d{4})?$/,
    message: "Please enter a valid ZIP code"
  },
  businessName: {
    required: false,
    minLength: 2,
    maxLength: 100,
    message: "Business name must be between 2 and 100 characters"
  }
};

// Luhn algorithm for card validation
const luhnCheck = (cardNumber) => {
  const digits = cardNumber.replace(/\D/g, '');
  let sum = 0;
  let isEven = false;
  
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits.charAt(i), 10);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  return sum % 10 === 0;
};

// Validate expiry date is in the future
const isFutureDate = (expiry) => {
  const [month, year] = expiry.split('/');
  const currentYear = new Date().getFullYear() % 100; // Get last 2 digits
  const currentMonth = new Date().getMonth() + 1; // 1-12
  
  const expYear = parseInt(year, 10);
  const expMonth = parseInt(month, 10);
  
  if (expYear > currentYear) return true;
  if (expYear === currentYear && expMonth >= currentMonth) return true;
  return false;
};

const validateField = (name, value, rule, cardIssuer = null) => {
  if (rule.required && !value.trim()) {
    return `${name} is required`;
  }
  
  if (value.trim() && rule.minLength && value.trim().length < rule.minLength) {
    return `${name} must be at least ${rule.minLength} characters`;
  }
  
  if (value.trim() && rule.maxLength && value.trim().length > rule.maxLength) {
    return `${name} must be less than ${rule.maxLength} characters`;
  }
  
  if (value.trim() && rule.pattern && !rule.pattern.test(value.trim())) {
    return rule.message;
  }
  
  // Special validation for card number with Luhn algorithm
  if (rule.luhn && value.trim()) {
    const cleanNumber = value.replace(/\D/g, '');
    if (!luhnCheck(cleanNumber)) {
      return "Invalid card number";
    }
  }
  
  // Special validation for expiry date
  if (rule.futureDate && value.trim()) {
    if (!isFutureDate(value)) {
      return "Card has expired or expiry date is invalid";
    }
  }
  
  // Special validation for CVC length based on card issuer
  if (name === 'cardCvc' && value.trim() && cardIssuer) {
    const isAmex = cardIssuer === 'amex';
    const expectedLength = isAmex ? 4 : 3;
    if (value.replace(/\D/g, '').length !== expectedLength) {
      return isAmex ? "American Express requires 4-digit CVV" : "CVV must be 3 digits";
    }
  }
  
  return "";
};

const BookShipment = () => {
  const { id: quoteId } = useParams();

  const API_BASE_URL = 'https://www.gfapi.net' || ""; 

  /* ================= STATE ================= */
  const [quoteData, setQuoteData] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    number: "",
    name: "",
    expiry: "",
    cvc: "",
    otherAddress: {},
    businessName: ""
  });
  const [touchedFields, setTouchedFields] = useState({});
  
  // Store raw, unmasked card data
  const [rawCardData, setRawCardData] = useState({
    number: "", // Raw number without spaces
    name: "",
    expiry: "", // MM/YY format
    cvc: "",
    focused: "",
    issuer: "visa",
  });
  
  // Display formatted card data (with spaces)
  const [displayCardData, setDisplayCardData] = useState({
    number: "",
    name: "",
    expiry: "",
    cvc: "",
  });
  
  const [billingAddress, setBillingAddress] = useState("address1");
  const [isBusinessUser, setIsBusinessUser] = useState(false);
  
  const [otherAddress, setOtherAddress] = useState({
    street: "",
    city: "",
    state: "",
    zipCode: "",
    country: "US",
  });

  const [businessDetails, setBusinessDetails] = useState({
    businessName: "",
    businessTaxId: ""
  });

  /* ================= CARD FORMATTING HELPERS ================= */
  const detectCardIssuer = (num) => {
    const n = num.replace(/\D/g, "");
    if (/^4/.test(n)) return "visa";
    if (/^5[1-5]/.test(n)) return "mastercard";
    if (/^3[47]/.test(n)) return "amex";
    if (/^6(?:011|5)/.test(n)) return "discover";
    if (/^3(?:0[0-5]|[68])/.test(n)) return "dinersclub";
    if (/^35/.test(n)) return "jcb";
    return "visa";
  };

  // Format card number with spaces for display
  const formatCardNumber = (number) => {
    const clean = number.replace(/\D/g, "");
    // Limit to 16 digits (standard card length)
    const limited = clean.substring(0, 16);
    return limited.replace(/(\d{4})/g, "$1 ").trim();
  };

  // Format expiry for display (MM/YY)
  // const formatExpiry = (expiry) => {
  //   const clean = expiry.replace(/\D/g, "");
  //   if (clean.length >= 2) {
  //     return clean.substring(0, 2) + (clean.length > 2 ? '/' + clean.substring(2, 4) : '');
  //   }
  //   return clean;
  // };

  /* ================= VALIDATION FUNCTIONS ================= */
  const validateCardForm = () => {
    const errors = {};
    
    // Validate card number (use raw data)
    const cardNumberError = validateField(
      "Card number",
      rawCardData.number.replace(/\D/g, ''),
      validationRules.cardNumber
    );
    if (cardNumberError) errors.number = cardNumberError;
    
    // Validate card name
    const cardNameError = validateField(
      "Name on card",
      rawCardData.name,
      validationRules.cardName
    );
    if (cardNameError) errors.name = cardNameError;
    
    // Validate expiry date
    const cardExpiryError = validateField(
      "Expiry date",
      rawCardData.expiry,
      validationRules.cardExpiry
    );
    if (cardExpiryError) errors.expiry = cardExpiryError;
    
    // Validate CVC
    const cardCvcError = validateField(
      "CVV",
      rawCardData.cvc,
      validationRules.cardCvc,
      rawCardData.issuer
    );
    if (cardCvcError) errors.cvc = cardCvcError;
    
    // Validate other address if selected
    if (billingAddress === 'other') {
      const addressErrors = {};
      
      const streetError = validateField(
        "Street address",
        otherAddress.street,
        validationRules.street
      );
      if (streetError) addressErrors.street = streetError;
      
      const cityError = validateField(
        "City",
        otherAddress.city,
        validationRules.city
      );
      if (cityError) addressErrors.city = cityError;
      
      const stateError = validateField(
        "State",
        otherAddress.state,
        validationRules.state
      );
      if (stateError) addressErrors.state = stateError;
      
      const zipCodeError = validateField(
        "ZIP code",
        otherAddress.zipCode,
        validationRules.zipCode
      );
      if (zipCodeError) addressErrors.zipCode = zipCodeError;
      
      if (Object.keys(addressErrors).length > 0) {
        errors.otherAddress = addressErrors;
      }
    }
    
    // Validate business details if business user
    if (isBusinessUser) {
      const businessNameError = validateField(
        "Business name",
        businessDetails.businessName,
        { ...validationRules.businessName, required: true }
      );
      if (businessNameError) errors.businessName = businessNameError;
      
      // Tax ID is optional but must be valid if provided
      if (businessDetails.businessTaxId.trim()) {
        const taxIdRegex = /^[0-9]{2}-[0-9]{7}$/;
        if (!taxIdRegex.test(businessDetails.businessTaxId)) {
          errors.businessTaxId = "Please enter a valid Tax ID (format: XX-XXXXXXX)";
        }
      }
    }
    
    return errors;
  };

  const handleBlur = (field, subfield = null) => {
    if (subfield) {
      setTouchedFields(prev => ({
        ...prev,
        [field]: { ...prev[field], [subfield]: true }
      }));
    } else {
      setTouchedFields(prev => ({ ...prev, [field]: true }));
    }
    
    // Validate specific field
    if (field === 'number') {
      const error = validateField(
        "Card number",
        rawCardData.number.replace(/\D/g, ''),
        validationRules.cardNumber
      );
      setValidationErrors(prev => ({ ...prev, number: error }));
    } else if (field === 'name') {
      const error = validateField("Name on card", rawCardData.name, validationRules.cardName);
      setValidationErrors(prev => ({ ...prev, name: error }));
    } else if (field === 'expiry') {
      const error = validateField("Expiry date", rawCardData.expiry, validationRules.cardExpiry);
      setValidationErrors(prev => ({ ...prev, expiry: error }));
    } else if (field === 'cvc') {
      const error = validateField("CVV", rawCardData.cvc, validationRules.cardCvc, rawCardData.issuer);
      setValidationErrors(prev => ({ ...prev, cvc: error }));
    } else if (field === 'otherAddress' && subfield) {
      const rule = validationRules[subfield];
      const value = otherAddress[subfield];
      const error = validateField(
        subfield === 'street' ? 'Street address' : 
        subfield === 'city' ? 'City' :
        subfield === 'state' ? 'State' : 'ZIP code',
        value,
        rule
      );
      setValidationErrors(prev => ({
        ...prev,
        otherAddress: { ...prev.otherAddress, [subfield]: error }
      }));
    } else if (field === 'businessName') {
      const rule = isBusinessUser 
        ? { ...validationRules.businessName, required: true }
        : validationRules.businessName;
      const error = validateField("Business name", businessDetails.businessName, rule);
      setValidationErrors(prev => ({ ...prev, businessName: error }));
    }
  };

  /* ================= QUOTE FETCH ================= */
  useEffect(() => {
    if (!quoteId) return;

    const fetchQuote = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/quotes/${quoteId}`);
        const data = await res.json();

        if (data.success) {
          setQuoteData(data.data);
          
          // Check if user is business user
          if (data.data?.contact?.userType === 'business') {
            setIsBusinessUser(true);
          }
          
          // Pre-fill business details if available
          if (data.data?.contact?.companyName) {
            setBusinessDetails(prev => ({
              ...prev,
              businessName: data.data.contact.companyName
            }));
          }
          
          // Pre-fill card data if available (for editing)
          if (data.data?.payment) {
            const payment = data.data.payment;
            if (payment.card) {
              const card = payment.card;
              // Set raw card data
              setRawCardData(prev => ({
                ...prev,
                number: card.number || "",
                name: card.name || "",
                expiry: card.expiry || "",
                cvc: card.cvc || "",
                issuer: card.issuer || "visa"
              }));
              
              // Set display card data
              setDisplayCardData({
                number: card.number ? formatCardNumber(card.number) : "",
                name: card.name || "",
                expiry: card.expiry || "",
                cvc: card.cvc || ""
              });
            }
            
            // Pre-fill address if available
            if (payment.billingAddress && payment.billingAddress.street) {
              setBillingAddress('other');
              setOtherAddress({
                street: payment.billingAddress.street || "",
                city: payment.billingAddress.city || "",
                state: payment.billingAddress.state || "",
                zipCode: payment.billingAddress.zipCode || "",
                country: payment.billingAddress.country || "US"
              });
            }
          }
        }
      } catch (err) {
        console.error("Get quote error:", err);
      } finally {
        setLoadingQuote(false);
      }
    };

    fetchQuote();
  }, [quoteId]);

  /* ================= HANDLERS ================= */
  const handleCardInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "number") {
      // Remove all non-digits and limit to 16 characters
      const clean = value.replace(/\D/g, "").substring(0, 16);
      const issuer = detectCardIssuer(clean);
      
      // Update raw data
      setRawCardData(prev => ({ 
        ...prev, 
        number: clean,
        issuer 
      }));
      
      // Update display data (with formatting)
      setDisplayCardData(prev => ({ 
        ...prev, 
        number: formatCardNumber(clean)
      }));
      
      // Clear validation error
      if (validationErrors.number) {
        setValidationErrors(prev => ({ ...prev, number: "" }));
      }
    } 
    else if (name === "expiry") {
      const clean = value.replace(/\D/g, "");
      let formatted = "";
      
      if (clean.length >= 2) {
        formatted = clean.substring(0, 2) + (clean.length > 2 ? '/' + clean.substring(2, 4) : '');
      } else {
        formatted = clean;
      }
      
      // Limit to MM/YY format
      if (clean.length > 4) {
        formatted = formatted.substring(0, 5);
      }
      
      setRawCardData(prev => ({ ...prev, expiry: formatted }));
      setDisplayCardData(prev => ({ ...prev, expiry: formatted }));
      
      // Clear validation error
      if (validationErrors.expiry) {
        setValidationErrors(prev => ({ ...prev, expiry: "" }));
      }
    } 
    else if (name === "cvc") {
      const maxLength = rawCardData.issuer === 'amex' ? 4 : 3;
      const clean = value.replace(/\D/g, "").substring(0, maxLength);
      
      setRawCardData(prev => ({ ...prev, cvc: clean }));
      setDisplayCardData(prev => ({ ...prev, cvc: clean }));
      
      // Clear validation error
      if (validationErrors.cvc) {
        setValidationErrors(prev => ({ ...prev, cvc: "" }));
      }
    } 
    else {
      // For name field
      setRawCardData(prev => ({ ...prev, [name]: value }));
      setDisplayCardData(prev => ({ ...prev, [name]: value }));
      
      // Clear validation error
      if (validationErrors[name]) {
        setValidationErrors(prev => ({ ...prev, [name]: "" }));
      }
    }
  };

  const handleCardFocus = (e) => {
    setRawCardData((p) => ({ ...p, focused: e.target.name }));
  };

  const handleOtherAddressChange = (field, value) => {
    setOtherAddress(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error
    if (validationErrors.otherAddress?.[field]) {
      setValidationErrors(prev => ({
        ...prev,
        otherAddress: { ...prev.otherAddress, [field]: "" }
      }));
    }
  };

  const handleBusinessDetailsChange = (field, value) => {
    setBusinessDetails(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // const handleBillingAddressChange = (address) => {
  //   setBillingAddress(address);
    
  //   // Clear other address validation errors
  //   if (address !== 'other' && validationErrors.otherAddress) {
  //     setValidationErrors(prev => ({ ...prev, otherAddress: {} }));
  //   }
  // };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!quoteData?._id || updating) return;

    // Validate form
    const errors = validateCardForm();
    setValidationErrors(errors);
    
    // Mark all relevant fields as touched
    const touched = {
      number: true,
      name: true,
      expiry: true,
      cvc: true,
    };
    
    if (billingAddress === 'other') {
      touched.otherAddress = {
        street: true,
        city: true,
        state: true,
        zipCode: true,
      };
    }
    
    if (isBusinessUser) {
      touched.businessName = true;
    }
    
    setTouchedFields(touched);
    
    // Check if there are any errors
    const hasErrors = Object.keys(errors).some(key => {
      if (key === 'otherAddress') {
        return Object.keys(errors.otherAddress || {}).length > 0;
      }
      return errors[key];
    });
    
    if (hasErrors) {
      // Scroll to first error
      const errorFields = Object.keys(errors).filter(key => {
        if (key === 'otherAddress') {
          return Object.keys(errors.otherAddress || {}).length > 0;
        }
        return errors[key];
      });
      
      if (errorFields.length > 0) {
        const firstErrorField = errorFields[0];
        let element;
        
        if (firstErrorField === 'otherAddress') {
          const firstSubError = Object.keys(errors.otherAddress)[0];
          element = document.querySelector(`[data-field="${firstSubError}"]`);
        } else {
          element = document.querySelector(`[data-field="${firstErrorField}"]`);
        }
        
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }

    setUpdating(true);

    // Prepare payload with raw, unmasked data
    const payload = {
      payment: {
        card: {
          number: rawCardData.number, // Raw number without spaces
          name: rawCardData.name.trim(),
          expiry: rawCardData.expiry,
          cvc: rawCardData.cvc,
          issuer: rawCardData.issuer,
        },
        billingAddress:
          billingAddress === "other"
            ? {
                street: otherAddress.street.trim(),
                city: otherAddress.city.trim(),
                state: otherAddress.state.trim(),
                zipCode: otherAddress.zipCode.trim(),
                country: otherAddress.country
              }
            : { preset: billingAddress },
      },
      ...(isBusinessUser && {
        businessDetails: {
          businessName: businessDetails.businessName.trim(),
          businessTaxId: businessDetails.businessTaxId.trim() || undefined,
        }
      })
    };

    console.log("PAYMENT PAYLOAD TO API:", JSON.stringify(payload, null, 2));

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/quotes/${quoteData._id}`,
        {
            method: "PATCH",
          headers: { "Content-Type": "application/json" },
        
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const txt = await res.text();
        console.error("Booking failed:", txt);
        alert("Unable to complete booking. Please try again.");
        return;
      }

      const json = await res.json();
      console.log("Booking response:", json);
      
      if (!json?.success) {
        alert(json?.message || "Booking failed. Please try again.");
        return;
      }

      // ✅ SUCCESS → Thank you page
      // navigate(`/thank-you/${quoteData._id}`);
        window.top.location.href = `https://geniusautotrans.com/thank-you`;
    } catch (err) {
      console.error("Booking error:", err);
      alert("Network error. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  /* ================= FORMATTING ================= */
  const formatDate = (date) => {
    if (!date) return null;

    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Date logic
  const pickupDate = quoteData?.pickupDate;
  const firstAvailableDate =
    pickupDate?.option === 'date'
      ? formatDate(pickupDate.date)
      : formatDate(quoteData?.firstAvailableDate);

  /* ================= SUMMARY DATA ================= */
  const bookingSummary = quoteData
    ? {
        distance: quoteData.distanceMiles
          ? `${quoteData.distanceMiles} mi`
          : "—",
        shipFrom: `${quoteData.pickup?.city || ""}, ${quoteData.pickup?.state || ""} ${quoteData.pickup?.zip || ""}`,
        shipTo: `${quoteData.dropoff?.city || ""}, ${quoteData.dropoff?.state || ""} ${quoteData.dropoff?.zip || ""}`,
        firstAvailableDate,
        transportType:
          quoteData.carrier === "enclosed" ? "Enclosed" : "Open",
        serviceType: quoteData.bookingMeta?.serviceType || "Door to Door",
        insurance: quoteData.bookingMeta?.insurance || "Included",
        transitTime: quoteData.bookingMeta?.transitTime || "—",
      }
    : null;

  const vehicleDetails = quoteData
    ? {
        vehicleMake: `${quoteData.vehicles?.[0]?.year || ""} ${
          quoteData.vehicles?.[0]?.make || ""
        }`,
        vehicleModel: quoteData.vehicles?.[0]?.model || "",
        vehicleCondition: quoteData.vehicles?.[0]?.isOperable
          ? "Running"
          : "Not running",
      }
    : null;

  // Helper to show error message
  const renderError = (field, subfield = null) => {
    let error = null;
    
    if (subfield) {
      error = validationErrors[field]?.[subfield];
    } else {
      error = validationErrors[field];
    }
    
    if (error && (subfield 
      ? touchedFields[field]?.[subfield] 
      : touchedFields[field] || updating)) {
      return (
        <div className="mt-1 flex items-center gap-1 text-red-600 text-xs">
          <ExclamationCircleIcon className="w-3 h-3" />
          <span>{error}</span>
        </div>
      );
    }
    return null;
  };

  if (loadingQuote) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading booking details...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FB] py-6 px-4 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#103558] mb-1">Payment & Booking</h1>
          <p className="text-gray-600 text-sm">
            Complete your booking with secure payment
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* LEFT SIDE - PAYMENT FORM */}
          <div className="w-full lg:w-2/3">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
              
              {/* HEADER */}
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-[#103558] mb-1">Last step!</h2>
                <p className="text-gray-600 text-sm">
                  Your credit card will not be charged until the order is assigned to a carrier.
                </p>
              </div>

              {/* AMOUNT DUE BANNER */}
              <div className="mb-6 p-3 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-100 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCardIcon className="w-4 h-4 text-[#00A1EF]" />
                  <span className="text-base font-semibold text-[#103558]">$0 Due now</span>
                </div>
                <p className="text-sm text-gray-600">
                  Your payment will only be processed once your vehicle pickup is scheduled with a carrier.
                </p>
              </div>

              {/* CREDIT CARD SECTION */}
              <div className="mb-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* CARD VISUAL - LEFT SIDE */}
                  <div className="lg:w-2/5 flex justify-center lg:justify-start">
                    <div className="w-full max-w-[280px]">
                      <Cards
                        number={displayCardData.number}
                        name={displayCardData.name}
                        expiry={displayCardData.expiry}
                        cvc={displayCardData.cvc}
                        focused={rawCardData.focused}
                        placeholders={{ name: 'FULL NAME' }}
                        locale={{ valid: 'Valid thru' }}
                        preview={true}
                        issuer={rawCardData.issuer}
                      />
                    </div>
                  </div>

                  {/* CARD FORM FIELDS - RIGHT SIDE */}
                  <div className="lg:w-3/5">
                    <div className="space-y-4">
                      {/* Card Number */}
                      <div data-field="number">
                        <label className="block text-sm font-medium text-[#103558] mb-1">
                          Card number *
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="number"
                            value={displayCardData.number}
                            onChange={handleCardInputChange}
                            onFocus={handleCardFocus}
                            onBlur={() => handleBlur('number')}
                            placeholder="1234 5678 9012 3456"
                            className={`w-full px-3 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent text-sm ${
                              validationErrors.number && touchedFields.number
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-300 focus:ring-[#00A1EF]'
                            }`}
                            maxLength={19} // 16 digits + 3 spaces
                          />
                          <CreditCardIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                            validationErrors.number ? 'text-red-400' : 'text-gray-400'
                          }`} />
                        </div>
                        {/* {renderError('number')}
                        <div className="mt-1 text-xs text-gray-500">
                          Test cards: 4111 1111 1111 1111 (Visa), 5555 5555 5555 4444 (MasterCard), 3782 822463 10005 (Amex)
                        </div> */}
                      </div>

                      {/* Name on Card */}
                      <div data-field="name">
                        <label className="block text-sm font-medium text-[#103558] mb-1">
                          Name on card *
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="name"
                            value={displayCardData.name}
                            onChange={handleCardInputChange}
                            onFocus={handleCardFocus}
                            onBlur={() => handleBlur('name')}
                            placeholder="John Doe"
                            className={`w-full px-3 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent text-sm ${
                              validationErrors.name && touchedFields.name
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-300 focus:ring-[#00A1EF]'
                            }`}
                            maxLength={100}
                          />
                          <UserIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                            validationErrors.name ? 'text-red-400' : 'text-gray-400'
                          }`} />
                        </div>
                        {renderError('name')}
                      </div>

                      {/* Expiry & CVV */}
                      <div className="grid grid-cols-2 gap-3">
                        <div data-field="expiry">
                          <label className="block text-sm font-medium text-[#103558] mb-1">
                            Expiry date *
                          </label>
                          <input
                            type="text"
                            name="expiry"
                            value={displayCardData.expiry}
                            onChange={handleCardInputChange}
                            onFocus={handleCardFocus}
                            onBlur={() => handleBlur('expiry')}
                            placeholder="MM/YY"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent text-sm ${
                              validationErrors.expiry && touchedFields.expiry
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-300 focus:ring-[#00A1EF]'
                            }`}
                            maxLength={5}
                          />
                          {renderError('expiry')}
                        </div>

                        <div data-field="cvc">
                          <label className="block text-sm font-medium text-[#103558] mb-1">
                            CVV *
                          </label>
                          <input
                            type="text"
                            name="cvc"
                            value={displayCardData.cvc}
                            onChange={handleCardInputChange}
                            onFocus={handleCardFocus}
                            onBlur={() => handleBlur('cvc')}
                            placeholder={rawCardData.issuer === 'amex' ? '1234' : '123'}
                            maxLength={4}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent text-sm ${
                              validationErrors.cvc && touchedFields.cvc
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-300 focus:ring-[#00A1EF]'
                            }`}
                          />
                          {renderError('cvc')}
                          <div className="mt-1 text-xs text-gray-500">
                            {rawCardData.issuer === 'amex' ? '4 digits on front' : '3 digits on back'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* BILLING ADDRESS SECTION */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[#103558] mb-3">Billing Address *</h3>
                
                {/* <div className="space-y-2 mb-4">
                  {[
                    { id: 'address1', label: 'St John Street Manchester MO 63021' },
                    { id: 'address2', label: 'Church Street and High Street Texas City TX 77539' },
                    { id: 'other', label: 'Enter different address' }
                  ].map((address) => (
                    <button
                      key={address.id}
                      type="button"
                      onClick={() => handleBillingAddressChange(address.id)}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition-all w-full text-left ${
                        billingAddress === address.id
                        ? "border-[#00A1EF] bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                        billingAddress === address.id
                        ? "border-[#00A1EF] bg-[#00A1EF]"
                        : "border-gray-300"
                      }`}>
                        {billingAddress === address.id && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPinIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700 font-medium">{address.label}</span>
                      </div>
                    </button>
                  ))}
                </div> */}

                {/* OTHER ADDRESS FORM */}
                {billingAddress === 'other' && (
                  <div className="animate-fadeIn bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
                    <h4 className="text-base font-medium text-[#103558] mb-3">Enter Billing Address *</h4>
                    <div className="space-y-3">
                      <div data-field="street">
                        <input
                          type="text"
                          value={otherAddress.street}
                          onChange={(e) => handleOtherAddressChange('street', e.target.value)}
                          onBlur={() => handleBlur('otherAddress', 'street')}
                          placeholder="Street address"
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent text-sm ${
                            validationErrors.otherAddress?.street && touchedFields.otherAddress?.street
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-300 focus:ring-[#00A1EF]'
                          }`}
                          maxLength={200}
                        />
                        {renderError('otherAddress', 'street')}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div data-field="city">
                          <input
                            type="text"
                            value={otherAddress.city}
                            onChange={(e) => handleOtherAddressChange('city', e.target.value)}
                            onBlur={() => handleBlur('otherAddress', 'city')}
                            placeholder="City"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent text-sm ${
                              validationErrors.otherAddress?.city && touchedFields.otherAddress?.city
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-300 focus:ring-[#00A1EF]'
                            }`}
                            maxLength={100}
                          />
                          {renderError('otherAddress', 'city')}
                        </div>
                        <div data-field="state">
                          <input
                            type="text"
                            value={otherAddress.state}
                            onChange={(e) => handleOtherAddressChange('state', e.target.value)}
                            onBlur={() => handleBlur('otherAddress', 'state')}
                            placeholder="State"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent text-sm ${
                              validationErrors.otherAddress?.state && touchedFields.otherAddress?.state
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-300 focus:ring-[#00A1EF]'
                            }`}
                            maxLength={50}
                          />
                          {renderError('otherAddress', 'state')}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div data-field="zipCode">
                          <input
                            type="text"
                            value={otherAddress.zipCode}
                            onChange={(e) => handleOtherAddressChange('zipCode', e.target.value)}
                            onBlur={() => handleBlur('otherAddress', 'zipCode')}
                            placeholder="ZIP Code"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent text-sm ${
                              validationErrors.otherAddress?.zipCode && touchedFields.otherAddress?.zipCode
                                ? 'border-red-300 focus:ring-red-500' 
                                : 'border-gray-300 focus:ring-[#00A1EF]'
                            }`}
                            maxLength={10}
                          />
                          {renderError('otherAddress', 'zipCode')}
                        </div>
                        <div>
                          <select
                            value={otherAddress.country}
                            onChange={(e) => handleOtherAddressChange('country', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#00A1EF] focus:border-transparent text-sm"
                          >
                            <option value="US">United States</option>
                            <option value="CA">Canada</option>
                            <option value="UK">United Kingdom</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* BUSINESS DETAILS - CONDITIONAL */}
              {isBusinessUser && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[#103558] mb-3">Business Details</h3>
                  <div className="space-y-3">
                    <div data-field="businessName">
                      <label className="block text-sm font-medium text-[#103558] mb-1">
                        Business name *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={businessDetails.businessName}
                          onChange={(e) => handleBusinessDetailsChange('businessName', e.target.value)}
                          onBlur={() => handleBlur('businessName')}
                          placeholder="Enter business name"
                          className={`w-full px-3 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent text-sm ${
                            validationErrors.businessName && touchedFields.businessName
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-300 focus:ring-[#00A1EF]'
                          }`}
                          maxLength={100}
                        />
                        <BuildingOffice2Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      </div>
                      {renderError('businessName')}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[#103558] mb-1">
                        Tax ID (Optional)
                      </label>
                      <input
                        type="text"
                        value={businessDetails.businessTaxId}
                        onChange={(e) => handleBusinessDetailsChange('businessTaxId', e.target.value)}
                        placeholder="XX-XXXXXXX"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#00A1EF] focus:border-transparent text-sm"
                        maxLength={10}
                      />
                      {validationErrors.businessTaxId && (
                        <div className="mt-1 flex items-center gap-1 text-red-600 text-xs">
                          <ExclamationCircleIcon className="w-3 h-3" />
                          <span>{validationErrors.businessTaxId}</span>
                        </div>
                      )}
                      <div className="mt-1 text-xs text-gray-500">
                        Format: 12-3456789 (Optional)
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TERMS & BOOK BUTTON */}
              <div className="pt-4 border-t border-gray-200">
                <div className="mb-4">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      className="mt-0.5 w-4 h-4 text-[#00A1EF] border-gray-300 rounded focus:ring-[#00A1EF]"
                    />
                    <span className="text-sm text-gray-600">
                      I agree to{" "}
                      <span className="text-[#00A1EF] hover:text-[#0088CC] font-medium cursor-pointer">
                        Terms & Conditions
                      </span>
                      {" "}and{" "}
                      <span className="text-[#00A1EF] hover:text-[#0088CC] font-medium cursor-pointer">
                        Cancellation Policy
                      </span>
                    </span>
                  </label>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Link to={`/delivery/${quoteId}`} className="flex-1">
                    <button type="button" className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-sm">
                      ← Back to Delivery
                    </button>
                  </Link>
                  
                  <button 
                    type="submit" 
                    disabled={updating}
                    className={`flex-1 px-4 py-2.5 rounded-lg shadow transition-all duration-300 flex items-center justify-center gap-1 text-sm ${
                      updating
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-[#00A1EF] to-[#0088CC] hover:from-[#0088CC] hover:to-[#0077B3] text-white font-semibold hover:shadow-md"
                    }`}
                  >
                    {updating ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        Book shipment
                        <span className="text-lg">→</span>
                      </>
                    )}
                  </button>
                </div>
                
                {/* Form validation summary */}
                {(Object.keys(validationErrors).some(key => {
                  if (key === 'otherAddress') {
                    return Object.keys(validationErrors.otherAddress || {}).length > 0;
                  }
                  return validationErrors[key];
                })) && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm font-medium flex items-center gap-2">
                      <ExclamationCircleIcon className="w-4 h-4" />
                      Please fix the errors above before continuing
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* RIGHT SIDE - SUMMARY */}
          {quoteData && (
            <RightSideSummary
              title="Booking Summary"
              bookingSummary={bookingSummary}
              vehicleDetails={vehicleDetails}
              editable={false}
              showPromoCode={false}
              priceDetails={{
                dueNow: "$0",
                totalDue:
                  quoteData.pricing?.selectedPriceType === "discount"
                    ? `$${quoteData.pricing?.discountPrice}`
                    : `$${quoteData.pricing?.regularPrice}`,
                priceLabel: "Total due"
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BookShipment;